/**
 * The two halves of "send the user back where they were after signing in": writing the
 * destination into the sign-in URL, and reading one back safely.
 */

/**
 * A post-sign-in destination that cannot leave this origin.
 *
 * The `?from=` value is attacker-settable via a crafted link, unlike the router state it
 * supplements. `//evil.com` is the shape that matters: it looks like a path but resolves as
 * protocol-relative, so handing it to the router throws a SecurityError on the sign-in page.
 * Both slash flavours are rejected on the second character — browsers normalise `\` to `/`
 * in http(s) URLs, so `/\evil.com` resolves off-origin exactly like `//evil.com`.
 */
export function safeRedirectPath(candidate: unknown): string | null {
  if (typeof candidate !== 'string') return null;
  if (!candidate.startsWith('/')) return null;
  if (candidate[1] === '/' || candidate[1] === '\\') return null;
  return candidate;
}

/**
 * Where to land after an expiry. The current path rides in the query string because the
 * interceptor navigates the document rather than pushing a route, so router state does not
 * survive it. Root and sign-in itself carry nothing, since there is nothing to come back to.
 */
export function signInUrlPreserving(loc: { pathname?: string; search?: string }): string {
  const pathname = loc.pathname ?? '';
  if (!pathname || pathname === '/' || pathname.startsWith('/sign-in')) return '/sign-in';
  return `/sign-in?from=${encodeURIComponent(`${pathname}${loc.search ?? ''}`)}`;
}
